// src/pages/outsourcing_management/requisitions/requisitionWorkflow.ts
//
// EE-207 #14, realigned in EE-253 - pure status-transition logic for the
// detail view's workflow action controls. Mirrors backend/controllers/
// purchasing/RequisitionWorkflowController.py exactly:
//
//   DRAFT -> SUBMITTED -> APPROVED -> AWAITING_SUPPLIER -> ORDERED
//         -> PICKUP_DELIVERY -> READY_TO_USE -> DONE
//
//   - submit: DRAFT -> SUBMITTED. The lines go into the buyer's Awaiting
//     Inquiry pool; no Inquiry is made (EE-253 P2b).
//   - reject: SUBMITTED -> DRAFT (mandatory comment), taking its lines off
//     any Draft Inquiry - refused once a line is on one that went out.
//   - close:  READY_TO_USE -> DONE, and closes the Inquiries as finished by
//     fan-in. Only once every line is ready to use and every Inquiry carrying
//     one is at Ready to Use. A line back in the pool blocks it: cancel is the exit
//     for a request nobody is buying any more.
//   - cancel: any unfinished status -> CLOSED (mandatory reason code).
//   - editable-fields (est. delivery date / supplier / comments): allowed on
//     any status except DRAFT (still a full-form edit) and the two terminal
//     ones (locked).
//
// DONE and CLOSED are both terminal and mean different things: close is the
// completion exit, cancel and reject are the early ones. Neither reopens a
// finished requisition.
//
// Everything from APPROVED to READY_TO_USE arrives from the linked Inquiry -
// approval included. There is no Approve action here any more (EE-253): the
// Inquiry is the document being approved, so approving is moving *it* out of
// Draft, which syncs the requisition to Approved. Advance and Convert are
// gone too.
//
// Every check here is a client-side mirror for hiding/disabling controls -
// the backend re-validates and is the actual source of truth (see
// _require_manager / status checks in the workflow controller).
import {
  RequisitionInquiryLinkType,
  RequisitionOptionsType,
  RequisitionStatusEnum,
} from "types/RequisitionTypes";
import { InquiryStatusEnum } from "types/InquiryTypes";

const Status = RequisitionStatusEnum;

export type RequisitionWorkflowAction =
  | "submit"
  | "reject"
  | "close"
  | "cancel";

// Finished with, whichever way it ended - mirrors CLOSED_STATUSES in
// backend/common/enums/RequisitionStatusEnum.py.
export const isFinished = (status: RequisitionStatusEnum): boolean =>
  status === Status.DONE || status === Status.CLOSED;

// Actions legal from a given status - mirrors the status checks in
// submit/reject/close in the workflow controller. Cancel is legal from every
// unfinished status.
// Line counts as RequisitionVM sends them (EE-253 P2b).
export type RequisitionLineCounts = {
  lines_total?: number;
  lines_in_enquiry?: number;
};

// Statuses a requisition with no lines at all (legacy) may be closed from:
// nothing else can ever move it.
const LEGACY_CLOSEABLE: RequisitionStatusEnum[] = [
  Status.APPROVED,
  Status.AWAITING_SUPPLIER,
  Status.ORDERED,
  Status.PICKUP_DELIVERY,
  Status.READY_TO_USE,
];

export const getAllowedActions = (
  status: RequisitionStatusEnum,
  inquiries: RequisitionInquiryLinkType[] = [],
  lines: RequisitionLineCounts = {}
): RequisitionWorkflowAction[] => {
  const actions: RequisitionWorkflowAction[] = [];
  if (status === Status.DRAFT) actions.push("submit");
  if (status === Status.SUBMITTED) actions.push("reject");
  // Shown at Ready to Use even while it would refuse - closeBlockedBecause
  // says why. The old exit for "every Inquiry finished" is gone: those lines
  // are back in the pool, and Done on top of them would sign off a request
  // nobody fulfilled (mirrors done_refusal).
  if (
    !isFinished(status) &&
    (status === Status.READY_TO_USE ||
      (lines.lines_total === 0 && LEGACY_CLOSEABLE.includes(status)))
  )
    actions.push("close");
  if (!isFinished(status)) actions.push("cancel");
  return actions;
};

// Mirrors done_refusal in backend/modules/purchasing/domain/composition.py.
// Marking the request done is what closes its inquiries, so they cannot be
// waited on to close first: an inquiry that has reached Ready to Use (or is
// closed already) is as far as it has to be.
export const hasUnfinishedInquiries = (
  inquiries: RequisitionInquiryLinkType[]
): boolean =>
  inquiries.some(
    (inquiry) =>
      inquiry.state !== InquiryStatusEnum.READY_TO_USE &&
      inquiry.state !== InquiryStatusEnum.CLOSED
  );

// Why "Mark Done" is there but refuses, when it is the Inquiry holding it up.
// Hiding the button at Ready to Use would read as the action being gone; the
// requester is standing exactly where they expect to sign the request off.
//
// Names the inquiries still short of Ready to Use. Ready to Use does not mean
// every line is: the inquiry gets there while some lines are still short, so
// the message says both.
export const closeBlockedBecause = (
  status: RequisitionStatusEnum,
  inquiries: RequisitionInquiryLinkType[] = [],
  lines: RequisitionLineCounts = {}
): string | undefined => {
  if (status !== Status.READY_TO_USE) return undefined;
  const waiting = (lines.lines_total ?? 0) - (lines.lines_in_enquiry ?? 0);
  if (waiting > 0)
    return `${waiting} line${waiting === 1 ? " is" : "s are"} waiting for an inquiry - put them on one, or cancel the requisition.`;
  if (!hasUnfinishedInquiries(inquiries)) return undefined;
  const names = inquiries
    .filter((inquiry) => hasUnfinishedInquiries([inquiry]))
    .map((inquiry) => inquiry.inquiry_no ?? `Inquiry #${inquiry.id}`)
    .join(", ");
  return `Waiting on ${names}: every line has to be ready to use before this request can be marked done.`;
};

// Edit-after-submit (#15) is legal on any status except DRAFT (full edit
// instead, via RequisitionCreateModal) and the terminal ones (locked) -
// mirrors update_editable_fields's two 409 checks.
export const canEditFields = (status: RequisitionStatusEnum): boolean =>
  status !== Status.DRAFT && !isFinished(status);

// The planned delivery date stops being editable at APPROVED - mirrors
// DELIVERY_DATE_LOCKED_FROM in RequisitionWorkflowController.py. Approval is
// the point the date is committed to: the requester plans around it and the
// status emails quote it, so letting it drift afterwards means the
// requisition disagrees with what everyone was told.
const DELIVERY_DATE_LOCK_ORDER: RequisitionStatusEnum[] = [
  Status.DRAFT,
  Status.SUBMITTED,
];

export const canEditDeliveryDate = (status: RequisitionStatusEnum): boolean =>
  DELIVERY_DATE_LOCK_ORDER.includes(status);

const ACTION_BUTTON_LABEL: Record<RequisitionWorkflowAction, string> = {
  submit: "Submit",
  reject: "Reject",
  // The button that completes a requisition, so it says so. It used to read
  // "Close Requisition", which was the same word cancel wrote into the
  // record - the two exits were indistinguishable on the screen as well as in
  // the data.
  close: "Mark Done",
  cancel: "Cancel",
};

export const getActionButtonLabel = (action: RequisitionWorkflowAction): string =>
  ACTION_BUTTON_LABEL[action];

// Cancellation reason options grouped by audience, in the sortOrder the
// backend already provides (Requester rows first, then Supply Chain).
export const groupCancellationReasonsByAudience = (
  options: RequisitionOptionsType | undefined
) => {
  const reasons = options?.cancellationReasons ?? [];
  const requester = reasons.filter((r) => r.audience === "REQUESTER");
  const supplyChain = reasons.filter((r) => r.audience === "SUPPLY_CHAIN");
  return { requester, supplyChain };
};
